export type Bi = { fr: string; en: string }

export type ChallengeStat = { value: Bi; label: Bi }

export type ChallengeBarrier = {
  key: 'process' | 'systems' | 'trust'
  number: string
  title: Bi
  stats: ChallengeStat[]
  source: Bi
  insight: Bi
}

export const atnaChallengeCopy = {
  eyebrow: { fr: 'LE DÉFI', en: 'THE CHALLENGE' },
  headline: [
    { text: { fr: 'Le défi n’est pas', en: 'The challenge isn’t' }, accent: false },
    { text: { fr: 'd’adopter l’IA.', en: 'adopting AI.' }, accent: false },
    { text: { fr: 'C’est de l’intégrer', en: 'It’s building it' }, accent: true },
    { text: { fr: 'aux opérations.', en: 'into operations.' }, accent: true },
  ],
  body: {
    fr: 'Les organisations font l’essai de l’IA, mais leurs processus, leurs données et leurs systèmes restent souvent fragmentés. Passer du copilote à une intelligence intégrée exige de repenser la façon dont le travail progresse.',
    en: 'Organizations are experimenting with AI, but their processes, data and systems often remain fragmented. Moving from a copilot to integrated intelligence requires rethinking how work moves forward.',
  },
  conclusion: {
    lead: { fr: 'L’IA seule ne transforme pas les opérations.', en: 'AI alone doesn’t transform operations.' },
    accent: [
      {
        fr: 'Il faut repenser les processus, connecter les données et les systèmes,',
        en: 'It takes rethinking processes, connecting data and systems,',
      },
      {
        fr: 'et créer les conditions de confiance pour passer du projet pilote à l’exploitation.',
        en: 'and creating the conditions of trust to move from pilot to operations.',
      },
    ],
  },
}

// External research — NOT BCITI | ATNA performance claims. Do not alter the figures.
export const atnaChallengeBarriers: ChallengeBarrier[] = [
  {
    key: 'process',
    number: '01',
    title: { fr: 'Processus', en: 'Processes' },
    stats: [
      {
        value: { fr: '21 %', en: '21%' },
        label: {
          fr: 'ont repensé en profondeur au moins certains de leurs flux de travail grâce à l’IA générative.',
          en: 'have fundamentally redesigned at least some workflows with GenAI.',
        },
      },
    ],
    source: { fr: 'McKinsey, 2025', en: 'McKinsey, 2025' },
    insight: {
      fr: 'Sans refonte des flux de travail, l’IA optimise souvent une tâche plutôt que le processus.',
      en: 'Without workflow redesign, AI often optimizes a task rather than the process.',
    },
  },
  {
    key: 'systems',
    number: '02',
    title: { fr: 'Systèmes et données', en: 'Systems & data' },
    stats: [
      {
        value: { fr: '53 %', en: '53%' },
        label: {
          fr: 'des dirigeants rapportent que les difficultés d’intégration de l’IA aux systèmes existants ont compromis l’atteinte des résultats visés.',
          en: 'of executives report that difficulties integrating AI with existing systems derailed the intended outcomes.',
        },
      },
    ],
    source: { fr: 'IBM Institute for Business Value, 2025', en: 'IBM Institute for Business Value, 2025' },
    insight: {
      fr: 'L’intelligence ne peut pas orchestrer ce qu’elle ne peut pas relier.',
      en: 'Intelligence can’t orchestrate what it can’t connect.',
    },
  },
  {
    key: 'trust',
    number: '03',
    title: { fr: 'Confiance et passage à l’échelle', en: 'Trust & scaling' },
    stats: [
      {
        value: { fr: '16 %', en: '16%' },
        label: {
          fr: 'ont déployé leurs initiatives d’IA à l’échelle de l’entreprise.',
          en: 'have scaled their AI initiatives across the enterprise.',
        },
      },
      {
        value: { fr: '25 %', en: '25%' },
        label: {
          fr: 'des initiatives d’IA avaient produit le rendement du capital investi (RCI) attendu.',
          en: 'of AI initiatives had delivered the expected ROI.',
        },
      },
    ],
    source: { fr: 'IBM CEO Study, 2025', en: 'IBM CEO Study, 2025' },
    insight: {
      fr: 'Passer du projet pilote à l’exploitation exige contrôle, gouvernance et confiance.',
      en: 'Moving from pilot to operations requires control, governance and trust.',
    },
  },
]
